import { Button } from '../../components/ui/Button';
import { Icon } from '../../components/ui/Icon';
import { Select } from '../../components/ui/Input';
import type { Actuador } from '../../../domain/entities/Actuador';
import type { ReglaActuador } from '../../../data/repositories/RepositorioUmbrales';
import estilos from './Umbrales.module.css';

interface EditorReglasActuadorProps {
  reglas: readonly ReglaActuador[];
  actuadores: readonly Actuador[];
  onCambiar: (reglas: ReglaActuador[]) => void;
}

/** Acciones que se pueden pedir a un actuador cuando salta la alarma. */
const ACCIONES = [
  { valor: 'encender', etiqueta: 'Encenderlo' },
  { valor: 'apagar', etiqueta: 'Apagarlo' },
  { valor: 'pulso', etiqueta: 'Darle un pulso' },
];

/** Traduce la orden guardada a la acción que se muestra en el desplegable. */
function accionDe(regla: ReglaActuador): string {
  if (regla.tipoOrden === 'pulse') return 'pulso';
  return String(regla.carga.v ?? '').toUpperCase() === 'OFF' ? 'apagar' : 'encender';
}

function cargaDe(accion: string): { tipoOrden: 'set' | 'pulse'; carga: Record<string, unknown> } {
  if (accion === 'pulso') return { tipoOrden: 'pulse', carga: { pulse_ms: 500 } };
  return { tipoOrden: 'set', carga: { v: accion === 'apagar' ? 'OFF' : 'ON' } };
}

/**
 * Órdenes automáticas que se envían a un actuador cuando el umbral se dispara.
 *
 * Es lo que convierte el sistema en algo que reacciona solo: por ejemplo,
 * encender una bomba cuando el nivel baja, o cortar una válvula ante una fuga.
 */
export function EditorReglasActuador({ reglas, actuadores, onCambiar }: EditorReglasActuadorProps) {
  const actualizar = (indice: number, cambios: Partial<ReglaActuador>) => {
    onCambiar(reglas.map((r, i) => (i === indice ? { ...r, ...cambios } : r)));
  };

  const agregar = () => {
    const primero = actuadores[0];
    if (!primero) return;

    onCambiar([
      ...reglas,
      {
        actuadorId: primero.id,
        actuadorNombre: primero.nombre,
        tipoOrden: 'set',
        carga: { v: 'ON' },
        restablecerAutomatico: false,
        activo: true,
      },
    ]);
  };

  return (
    <div className={estilos.seccionAnexa}>
      <div className={estilos.seccionCabecera}>
        <div>
          <strong>Acciones automáticas</strong>
          <span className={estilos.opcionAyuda}>
            {reglas.length === 0
              ? 'Al dispararse sólo se avisa; no se acciona ningún equipo.'
              : 'Estos equipos se accionarán solos al dispararse la alarma.'}
          </span>
        </div>
        <Button
          tamano="sm"
          variante="secundario"
          icono="agregar"
          motivoDeshabilitado={
            actuadores.length === 0 ? 'No hay actuadores disponibles para accionar' : null
          }
          onClick={agregar}
        >
          Añadir acción
        </Button>
      </div>

      {reglas.map((regla, indice) => (
        <div key={regla.id ?? indice} className={estilos.regla}>
          <div className={estilos.reglaCampos}>
            <Select
              etiqueta="Equipo"
              value={regla.actuadorId}
              onChange={(e) => {
                const id = Number(e.target.value);
                const actuador = actuadores.find((a) => a.id === id);
                actualizar(indice, {
                  actuadorId: id,
                  actuadorNombre: actuador?.nombre ?? `Actuador ${id}`,
                });
              }}
              opciones={actuadores.map((a) => ({
                valor: a.id,
                etiqueta: `${a.nombre}${a.nodoNombre ? ` · ${a.nodoNombre}` : ''}`,
              }))}
            />

            <Select
              etiqueta="Qué hacer"
              value={accionDe(regla)}
              onChange={(e) => actualizar(indice, cargaDe(e.target.value))}
              opciones={ACCIONES}
            />

            <Button
              tamano="sm"
              variante="fantasma"
              icono="eliminar"
              title="Quitar acción"
              onClick={() => onCambiar(reglas.filter((_, i) => i !== indice))}
            />
          </div>

          <label className={estilos.opcion}>
            <input
              type="checkbox"
              checked={regla.restablecerAutomatico}
              onChange={(e) => actualizar(indice, { restablecerAutomatico: e.target.checked })}
            />
            <span>
              <strong>Deshacer al volver a normal</strong>
              <span className={estilos.opcionAyuda}>
                Cuando la condición se normalice, el equipo vuelve solo a su estado anterior.
              </span>
            </span>
          </label>

          {!regla.activo && (
            <span className={estilos.avisoMenor}>
              <Icon nombre="advertenciaContorno" tamano={13} />
              Esta acción está desactivada y no se ejecutará.
            </span>
          )}
        </div>
      ))}
    </div>
  );
}
